import { useEffect, useRef, useState } from "react";
import type { PlanInputs, SolveResult } from "../model/types.ts";
import type { WorkerRequest, WorkerResponse } from "../worker/sim.worker.ts";

/**
 * Runs the solve in a Web Worker so a 20,000-path Monte Carlo never blocks
 * typing. Requests are debounced and stale replies are dropped.
 */
export function usePlan(inputs: PlanInputs, debounceMs = 350) {
  const [result, setResult] = useState<SolveResult | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const latestId = useRef(0);

  useEffect(() => {
    const worker = new Worker(new URL("../worker/sim.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      if (e.data.id !== latestId.current) return; // a newer request is in flight
      setBusy(false);
      if (e.data.ok) {
        setResult(e.data.result);
        setError(null);
      } else {
        setError(e.data.error);
      }
    };
    worker.onerror = (e) => {
      setBusy(false);
      setError(e.message || "The calculation failed to start.");
    };
    workerRef.current = worker;
    return () => worker.terminate();
  }, []);

  useEffect(() => {
    setBusy(true);
    const timer = setTimeout(() => {
      const worker = workerRef.current;
      if (!worker) return;
      const req: WorkerRequest = { id: ++latestId.current, inputs };
      worker.postMessage(req);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [inputs, debounceMs]);

  return { result, busy, error };
}
